import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useEvents } from "../hooks/use-events";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { format, parseISO, isSameDay, isSameMonth, isSameYear } from "date-fns";
import { IconCalendar, IconClock, IconSparkles } from '@tabler/icons-react';

interface EventsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDateRange = (startDate: string, endDate: string): string => {
  try {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    
    if (isSameDay(start, end)) {
      return format(start, "MMMM d, yyyy");
    }
    
    if (isSameYear(start, end) && isSameMonth(start, end)) {
      return `${format(start, "MMMM d")} - ${format(end, "d, yyyy")}`;
    }
    
    if (isSameYear(start, end)) {
      return `${format(start, "MMMM d")} - ${format(end, "MMMM d, yyyy")}`;
    }
    
    return `${format(start, "MMMM d, yyyy")} - ${format(end, "MMMM d, yyyy")}`;
  } catch {
    return `${startDate} - ${endDate}`;
  }
};

export const EventsSheet = ({ open, onOpenChange }: EventsSheetProps) => {
  const { data: events, isLoading, error } = useEvents();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:w-[500px] p-0 flex flex-col overflow-hidden">
        <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
          <SheetTitle className="text-2xl">Events & Activities</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="px-6 py-8 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <Separator />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center px-6 py-16">
              <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-center">
                <p className="text-destructive font-medium mb-1">Failed to load events</p>
                <p className="text-sm text-muted-foreground">Please try again later</p>
              </div>
            </div>
          )}

          {!isLoading && !error && events?.length === 0 && (
            <div className="flex flex-col items-center justify-center px-6 py-16">
              <div className="p-6 rounded-lg bg-muted/50 border border-border text-center">
                <IconSparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium mb-1">No events scheduled</p>
                <p className="text-sm text-muted-foreground">Check back soon for upcoming events</p>
              </div>
            </div>
          )}

          {!isLoading && !error && events && events.length > 0 && (
            <div className="px-6 py-6 space-y-6">
              {events.map((event, index) => (
                <div key={event.id}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 mt-0.5 flex-shrink-0 flex items-center justify-center">
                      <IconCalendar className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-lg font-bold text-foreground leading-tight">
                          {event.EventTitle}
                        </h3>
                        {event.event_type && (
                          <Badge variant="default" className="text-xs text-primary-foreground flex-shrink-0">
                            {event.event_type.EventType}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <IconClock className="h-4 w-4 flex-shrink-0" />
                        <span>{formatDateRange(event.EventStartDate, event.EventEndDate)}</span>
                      </div>
                      {event.EventDetails && (
                        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {event.EventDetails}
                        </p>
                      )}
                    </div>
                  </div>
                  {index < events.length - 1 && <Separator className="mt-6 h-px bg-border/50" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

